import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useTheme } from "../../../context/ThemeContext";

const BRANDED = { dark: "#FF43A7", light: "#150029" };
const PRINT = { dark: "#1E0039", light: "#FFFFFF" };

function triggerDownload(href, filename) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// compact: single branded QR + Save PNG (waitlist "share in person"). Full: branded/print
// toggle + PNG/SVG/poster downloads (EPK share modal).
//
// Chromium drops a download triggered from a click handler once an `await` breaks the
// user-activation chain, so every export is pre-generated on mode/value change and the
// click handlers just hand off an already-ready payload - no async work in the handler.
export default function ShareQrCode({ value, compact = false, size = 132 }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [mode, setMode] = useState("dark");
  const [dataUrl, setDataUrl] = useState("");
  const [hiResPng, setHiResPng] = useState("");
  const [svgBlobUrl, setSvgBlobUrl] = useState("");
  // compact has no Branded/Print toggle of its own, so it follows the app's light/dark
  // theme instead - otherwise it renders a fixed dark panel that has no light-mode
  // override and turns into a near-black block on a white modal.
  const activeMode = compact ? (theme === "light" ? "light" : "dark") : mode;
  const colors = activeMode === "dark" ? BRANDED : PRINT;

  useEffect(() => {
    let cancelled = false;
    // Stale-payload guard: a click mid-regeneration would otherwise silently download
    // the previous mode's asset.
    setHiResPng("");
    setSvgBlobUrl("");

    QRCode.toDataURL(value, { width: size * 4, margin: 1, color: colors })
      .then((url) => { if (!cancelled) setDataUrl(url); })
      .catch(() => {});

    if (!compact) {
      QRCode.toDataURL(value, { width: 1024, margin: 1, color: colors })
        .then((url) => { if (!cancelled) setHiResPng(url); })
        .catch(() => {});

      QRCode.toString(value, { type: "svg", margin: 1, color: colors })
        .then((svg) => {
          if (cancelled) return;
          setSvgBlobUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
          });
        })
        .catch(() => {});
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, activeMode, size, compact]);

  useEffect(() => () => { if (svgBlobUrl) URL.revokeObjectURL(svgBlobUrl); }, [svgBlobUrl]);

  const downloadPng = () => {
    if (!hiResPng) return toast.error(t("Failed to generate QR image"));
    triggerDownload(hiResPng, "kinoklik-qr.png");
  };

  const downloadSvg = () => {
    if (!svgBlobUrl) return toast.error(t("Failed to generate QR image"));
    triggerDownload(svgBlobUrl, "kinoklik-qr.svg");
  };

  if (compact) {
    const isDark = activeMode === "dark";
    return (
      <div
        className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-p-3"
        style={{
          background: isDark ? "#150029" : "#F6F2F8",
          borderColor: isDark ? "rgba(255,67,167,.3)" : "rgba(255,176,207,.25)",
          boxShadow: isDark ? "inset 0 0 26px rgba(255,67,167,0.12)" : "none",
        }}
      >
        <div style={{ width: size, height: size }} className="tw-flex tw-items-center tw-justify-center">
          {dataUrl && <img src={dataUrl} alt={t("QR code")} width={size} height={size} />}
        </div>
        <button
          type="button"
          onClick={() => (dataUrl ? triggerDownload(dataUrl, "kinoklik-qr.png") : toast.error(t("Failed to generate QR image")))}
          className="tw-h-9 tw-w-full tw-rounded-lg tw-border tw-cursor-pointer tw-bg-transparent tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-wider"
          style={{ borderColor: "rgba(170,136,148,.3)", color: isDark ? "#F0DBFF" : "#1e0039" }}
        >
          {t("Save PNG")}
        </button>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2.5">
      <div className="tw-flex tw-items-baseline tw-justify-between">
        <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-[#1e0039]/70">
          {t("QR code")}
        </span>
      </div>

      <div className="tw-flex tw-gap-1.5">
        <button
          type="button"
          onClick={() => setMode("dark")}
          className={`tw-flex-1 tw-h-7 tw-rounded-md tw-text-[9.5px] tw-font-extrabold tw-uppercase tw-tracking-wide tw-cursor-pointer ${
            mode === "dark"
              ? "tw-bg-[#FF43A7]/20 tw-border tw-border-[#FF43A7] tw-text-[#FF43A7]"
              : "tw-bg-transparent tw-border tw-border-[#AA8894]/40 tw-text-[#1e0039]/60"
          }`}
        >
          {t("Branded")}
        </button>
        <button
          type="button"
          onClick={() => setMode("light")}
          className={`tw-flex-1 tw-h-7 tw-rounded-md tw-text-[9.5px] tw-font-extrabold tw-uppercase tw-tracking-wide tw-cursor-pointer ${
            mode === "light"
              ? "tw-bg-[#FF43A7]/20 tw-border tw-border-[#FF43A7] tw-text-[#FF43A7]"
              : "tw-bg-transparent tw-border tw-border-[#AA8894]/40 tw-text-[#1e0039]/60"
          }`}
        >
          {t("Print")}
        </button>
      </div>

      <div
        className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-xl tw-p-3"
        style={{
          background: mode === "dark" ? "#150029" : "#F6F2F8",
          border: `1px solid ${mode === "dark" ? "rgba(255,67,167,.3)" : "rgba(255,176,207,.25)"}`,
        }}
      >
        <div style={{ width: size, height: size }} className="tw-flex tw-items-center tw-justify-center">
          {dataUrl && <img src={dataUrl} alt={t("QR code")} width={size} height={size} />}
        </div>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-1.5">
        <button
          type="button"
          onClick={downloadPng}
          className="tw-h-9 tw-rounded-lg tw-bg-[#FF43A7] tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-wide tw-text-white tw-border-none tw-cursor-pointer"
        >
          {t("Download PNG")}
        </button>
        <button
          type="button"
          onClick={downloadSvg}
          className="tw-h-9 tw-rounded-lg tw-border tw-border-[#AA8894]/30 tw-bg-transparent tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-wide tw-text-[#1e0039] tw-cursor-pointer"
        >
          {t("Download SVG")}
        </button>
      </div>
    </div>
  );
}
